import { Component, NgZone, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { FeatureCollection } from 'geojson';
import 'leaflet';
import { LatLng, Marker } from 'leaflet';
import 'leaflet-extra-markers';
import 'leaflet-routing-machine';

import { DetailsBase } from 'app/locations/details/detailsbase';
import { AlertcPoint, Siterecord, SiterecordComment, VildVersion } from 'app/models';
import { ConfigurationStatusType, DataProviderType, VildEncodingStatusType } from 'app/models/enums';
import { ShapeStyle } from 'app/models/shapestyle.model';
import { SiterecordCoordinatesChange } from 'app/models/siterecord-coordinates-change.model';
import {
  AuthorizationService,
  ErrorService,
  MapService,
  SiterecordCommentService,
  SiterecordService,
  StorageService,
  VildVersionService
} from 'app/services';
import { DialogCaptureCommentComponent } from 'app/shared/dialog-capture-comment/dialog-capture-comment.component';
import { DialogConfirmationComponent } from 'app/shared/dialog-delete-location/dialog-confirmation.component';
import { DialogEditCoordinatesComponent } from 'app/shared/dialog-edit-coordinates/dialog-edit-coordinates.component';
import { DialogEditLocationIdComponent } from 'app/shared/dialog-edit-location-id/dialog-edit-location-id.component';
import { PointDetailsVildComponent } from 'app/shared/point-details/vild/point-details-vild.component';

declare const L: any;

@Component({
  selector: 'app-point-details',
  templateUrl: './point-details.component.html',
  styleUrls: ['./point-details.component.scss']
})
export class PointDetailsComponent extends DetailsBase implements OnInit {
  @ViewChildren(PointDetailsVildComponent) pointDetailsVild: QueryList<PointDetailsVildComponent>;

  @ViewChild('dialogEditLocationIdComponent') dialogEditLocationIdComponent: DialogEditLocationIdComponent;
  @ViewChild('dialogEditCoordinatesComponent') dialogEditCoordinatesComponent: DialogEditCoordinatesComponent;
  @ViewChild('confirmDeleteRollbackDialog') dialogConfirmationComponent: DialogConfirmationComponent;
  @ViewChild('dialogCaptureCommentComponent') dialogCaptureCommentComponent: DialogCaptureCommentComponent;

  private routingPointsLayer: L.Layer;
  private previousRoutingPointsLayer: L.Layer;

  private adminRoutingPointsLayer: L.Layer;
  private adminRoutingPointsLayerGroup: L.LayerGroup;
  private pointShapeLayerGroup: L.LayerGroup;

  private previousPointShapeLayerGroup: L.LayerGroup;
  private routingPointsTranslation: string;
  private previousRoutingPointsTranslation: string;

  private adminRoutingPointsTranslation: string;
  private adminLine: any;
  private adminArrowHead: any;
  private pointShapeLine: any;
  private pointShapeArrowHead: any;
  private previousPointShapeLine: any;

  private previousPointsShapeArrowHead: any;

  private isCoordinateEditMode = false;

  constructor(
    route: ActivatedRoute,
    router: Router,
    authorizationService: AuthorizationService,
    siterecordService: SiterecordService,
    siterecordCommentService: SiterecordCommentService,
    storageService: StorageService,
    translateService: TranslateService,
    mapService: MapService,
    vildVersionService: VildVersionService,
    private readonly errorService: ErrorService,
    private readonly ngZone: NgZone
  ) {
    super(
      route,
      router,
      authorizationService,
      siterecordService,
      siterecordCommentService,
      storageService,
      translateService,
      mapService,
      vildVersionService
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.errorService.pointDetailsComponent = this;
  }

  protected initializeControl(draggable: boolean, shapeStyle?: ShapeStyle): void {
    if (!shapeStyle) {
      shapeStyle = this.SHAPE_STYLE;
    }
    if (this.control) {
      this.map.removeControl(this.control);
      this.control = null;
    }

    this.map.createPane(this.NEW_SHAPE_STYLE.pane);
    this.map.getPane(this.NEW_SHAPE_STYLE.pane).style.zIndex = '530';

    this.control = L.Routing.control({
      router: new L.Routing.PgRouting('cbmdb', { serviceUrl: this.mapService.geoserverWfsUrl }),
      waypointMode: 'snap',
      routeWhileDragging: false,
      fitSelectedRoutes: true,
      autoRoute: false,
      createMarker: (i, dStart, n) => {
        // Create marker when route is found
        if (!this.readonly && this.isAllowedToEditRoute() && i === 0) {
          return this.createMarker(dStart.latLng, shapeStyle, draggable);
        }
      },
      routeLine: route => {
        // Dont show the route line
        return L.polyline(route.coordinates, {
          weight: 0
        });
      }
    }).addTo(this.map);

    this.control.on('routeselected', e => {
      const waypoints = this.control.getWaypoints();
      let routeFound = true;

      if (waypoints[0].latLng != null && waypoints[waypoints.length - 1].latLng != null) {
        this.onRouteSelected(e);
      } else {
        routeFound = false;
      }

      if (routeFound) {
        this.previousWaypoints = [];
        for (const waypoint of waypoints) {
          this.previousWaypoints.push(L.latLng(waypoint.latLng.lat, waypoint.latLng.lng));
        }
      } else if (!routeFound) {
        this.errorFindingRoute();
      }
    });

    this.control.on('routingerror', e => this.errorFindingRoute());
  }

  private onRouteSelected(e: any): void {
    // Create arrow instead of route line
    let shapeStyle;
    if (this.isContractDataproviderTypeMst()) {
      shapeStyle = this.ADMIN_ROUTING_POINTS_STYLE;
    } else {
      shapeStyle = this.NEW_SHAPE_STYLE;
    }
    this.line = this.createLine([e.route.coordinates[0], e.route.coordinates[1]], shapeStyle).addTo(this.map);
    this.arrowHead = this.createArrowHead(this.line, shapeStyle).addTo(this.map);

    this.location.bearing = this.calculateBearing(
      e.route.coordinates[0].lat,
      e.route.coordinates[0].lng,
      e.route.coordinates[1].lat,
      e.route.coordinates[1].lng
    );
    if (this.isContractDataproviderTypeMst()) {
      this.location.adminRoutingPoints = `${e.route.coordinates[0].lng}\\,${e.route.coordinates[0].lat}|${e.route.coordinates[1].lng}\\,${e.route.coordinates[1].lat}`;
    } else {
      this.location.routingPoints = `${e.route.coordinates[0].lng}\\,${e.route.coordinates[0].lat}|${e.route.coordinates[1].lng}\\,${e.route.coordinates[1].lat}`;
    }
  }

  public errorFindingRoute(): void {
    if (this.previousWaypoints && this.previousWaypoints.length > 0) {
      this.control.setWaypoints(this.previousWaypoints);
    }
    alert(this.noRouteFoundTranslation);
  }

  public updateCoordinates(changes: SiterecordCoordinatesChange) {
    this.isCoordinateEditMode = true;

    if (changes.lat && changes.lon && changes.bearing) {
      const directionCoordinateResult = this.calculateDirectionCoordinatePair(
        changes.lat,
        changes.lon,
        changes.bearing
      );
      this.location.routingPoints = `${directionCoordinateResult}`;

      const coordinates = this.getCoordinatesFromRoutingPoints(this.location.routingPoints);

      this.control.setWaypoints([L.latLng(coordinates[0]), L.latLng(coordinates[1])]);
      this.map.panTo(coordinates[0], { animate: false });
    }

    this.isCoordinateEditMode = false;
  }

  private drawPoints(): void {
    if (this.location.routingPoints && this.isContractDataproviderTypeMst()) {
      this.map.createPane(this.ROUTING_POINTS_STYLE.pane);
      this.map.getPane(this.ROUTING_POINTS_STYLE.pane).style.zIndex = '525';
      this.routingPointsLayer = this.createMarker(
        this.getStartCoordinateFromRoutingPoints(this.location.routingPoints),
        this.ROUTING_POINTS_STYLE
      );
      this.featureGroup.addLayer(this.routingPointsLayer);
      this.layerControl.addOverlay(this.routingPointsLayer, this.routingPointsTranslation);
    }

    if (this.location.previousRoutingPoints && this.isContractDataproviderTypeMst()) {
      this.map.createPane(this.PREVIOUS_ROUTING_POINTS_STYLE.pane);
      this.map.getPane(this.PREVIOUS_ROUTING_POINTS_STYLE.pane).style.zIndex = '520';
      this.previousRoutingPointsLayer = this.createMarker(
        this.getStartCoordinateFromRoutingPoints(this.location.previousRoutingPoints),
        this.PREVIOUS_ROUTING_POINTS_STYLE
      );
      this.featureGroup.addLayer(this.previousRoutingPointsLayer);
      this.layerControl.addOverlay(this.previousRoutingPointsLayer, this.previousRoutingPointsTranslation);
    }

    if (this.location.pointShape) {
      this.map.createPane(this.SHAPE_STYLE.pane);
      this.map.getPane(this.SHAPE_STYLE.pane).style.zIndex = '515';
      this.pointShapeLayerGroup = new L.LayerGroup();
      this.layerControl.addOverlay(this.pointShapeLayerGroup, this.shapeTranslation);

      this.mapService.getGeoJson('siterecord:pointshape', { siterecord_id: this.location.id }).subscribe(
        (response: FeatureCollection) => {
          const startCoordinate = this.getStartCoordinateFromShape(this.location.pointShape);
          const bearingCoordinates = response.features[0].properties.point_shape_bearing.coordinates;
          const coordinates = [startCoordinate, L.latLng(bearingCoordinates[1], bearingCoordinates[0])];

          this.shapeLayer = this.createMarker(startCoordinate, this.SHAPE_STYLE);
          this.pointShapeLine = this.createLine(coordinates, this.SHAPE_STYLE);
          this.pointShapeArrowHead = this.createArrowHead(this.pointShapeLine, this.SHAPE_STYLE);

          this.pointShapeLayerGroup.addLayer(this.shapeLayer);
          this.pointShapeLayerGroup.addLayer(this.pointShapeLine);
          this.pointShapeLayerGroup.addLayer(this.pointShapeArrowHead);

          this.pointShapeLayerGroup.addTo(this.map);
        },
        error => console.log(error)
      );
    }

    if (this.location.previousPointShape) {
      this.map.createPane(this.PREVIOUS_SHAPE_STYLE.pane);
      this.map.getPane(this.PREVIOUS_SHAPE_STYLE.pane).style.zIndex = '510';
      this.previousPointShapeLayerGroup = new L.LayerGroup();
      this.layerControl.addOverlay(this.previousPointShapeLayerGroup, this.previousShapeTranslation);

      this.mapService.getGeoJson('siterecord:pointshape_previous', { siterecord_id: this.location.id }).subscribe(
        (response: FeatureCollection) => {
          const startCoordinates = this.getStartCoordinateFromShape(this.location.previousPointShape);
          const bearingCoordinates = response.features[0].properties.previous_point_shape_bearing.coordinates;
          const coordinates = [startCoordinates, L.latLng(bearingCoordinates[1], bearingCoordinates[0])];

          this.previousShapeLayer = this.createMarker(startCoordinates, this.PREVIOUS_SHAPE_STYLE);
          this.previousPointShapeLine = this.createLine(coordinates, this.PREVIOUS_SHAPE_STYLE);
          this.previousPointsShapeArrowHead = this.createArrowHead(
            this.previousPointShapeLine,
            this.PREVIOUS_SHAPE_STYLE
          );

          this.previousPointShapeLayerGroup.addLayer(this.previousShapeLayer);
          this.previousPointShapeLayerGroup.addLayer(this.previousPointShapeLine);
          this.previousPointShapeLayerGroup.addLayer(this.previousPointsShapeArrowHead);

          this.previousPointShapeLayerGroup.addTo(this.map);
        },
        error => console.log(error)
      );
    }

    if (this.location.adminRoutingPoints && this.isContractDataproviderTypeMst()) {
      this.map.createPane(this.ADMIN_ROUTING_POINTS_STYLE.pane);
      this.map.getPane(this.ADMIN_ROUTING_POINTS_STYLE.pane).style.zIndex = '505';

      this.adminRoutingPointsLayer = this.createMarker(
        this.getStartCoordinateFromRoutingPoints(this.location.adminRoutingPoints),
        this.ADMIN_ROUTING_POINTS_STYLE
      );
      this.adminLine = this.createLine(
        this.getCoordinatesFromRoutingPoints(this.location.adminRoutingPoints),
        this.ADMIN_ROUTING_POINTS_STYLE
      );
      this.adminArrowHead = this.createArrowHead(this.adminLine, this.ADMIN_ROUTING_POINTS_STYLE);

      this.adminRoutingPointsLayerGroup = L.layerGroup([
        this.adminRoutingPointsLayer,
        this.adminLine,
        this.adminArrowHead
      ]);
      this.adminRoutingPointsLayerGroup.addTo(this.map);
      this.layerControl.addOverlay(this.adminRoutingPointsLayerGroup, this.adminRoutingPointsTranslation);
    }

    this.featureGroup.addTo(this.map);
    const bounds: L.LatLngBounds = this.featureGroup.getBounds();
    if (bounds && Object.keys(bounds).length > 0) {
      this.map.fitBounds(bounds);
    } else {
      const routingBounds = L.latLngBounds(
        this.location.routingPoints.split('|').map(rp => {
          const lngLat = rp.replace('\\', '').split(',');
          return L.latLng(lngLat[1], lngLat[0]);
        })
      );
      this.map.fitBounds(routingBounds);
    }
  }

  private createLine(coordinates: any, shapeStyle: ShapeStyle): any {
    return L.polyline(coordinates, { color: shapeStyle.lineColor, pane: shapeStyle.pane });
  }

  private createArrowHead(line: any, shapeStyle: ShapeStyle): any {
    return L.polylineDecorator(line, {
      patterns: [
        {
          offset: '100%',
          repeat: 0,
          symbol: L.Symbol.arrowHead({ pixelSize: 15, polygon: false, pathOptions: { stroke: true, shapeStyle } })
        }
      ],
      pane: shapeStyle.pane
    });
  }

  private createMarker(latLng: LatLng, shapeStyle: ShapeStyle, draggable?: boolean): Marker {
    if (!draggable) {
      draggable = false;
    }

    const markerIcon = L.ExtraMarkers.icon({
      markerColor: shapeStyle.markerColor,
      shape: 'circle'
    });

    return new L.Marker(latLng, {
      draggable,
      icon: markerIcon,
      pane: shapeStyle.pane
    });
  }

  private deleteLineAndArrowHead(): void {
    if (this.map.hasLayer(this.line)) {
      this.map.removeLayer(this.arrowHead);
      this.map.removeLayer(this.line);
      this.arrowHead = null;
      this.line = null;
    }
  }

  private getStartCoordinateFromRoutingPoints(routingPoints: any): any {
    const rp = routingPoints.split('|');
    const lngLat = rp[0].replace('\\', '').split(',');
    return L.latLng(lngLat[1], lngLat[0]);
  }

  private getCoordinatesFromRoutingPoints(routingPoints: any): any {
    return routingPoints.split('|').map(rp => {
      const lngLat = rp.replace('\\', '').split(',');
      return L.latLng(lngLat[1], lngLat[0]);
    });
  }

  private getStartCoordinateFromShape(shape: any): L.LatLng {
    const linestringRegexp = /\((.*?)\)/g;
    const points = linestringRegexp.exec(shape.toString())[1].split(', ');
    const lngLat = points[0].split(' ');
    return L.latLng(parseFloat(lngLat[1]), parseFloat(lngLat[0]));
  }

  private createRoute(): void {
    this.deleteLineAndArrowHead();

    let coordinates;
    if (this.location.adminRoutingPoints) {
      coordinates = this.getCoordinatesFromRoutingPoints(this.location.adminRoutingPoints);
    } else {
      coordinates = this.getCoordinatesFromRoutingPoints(this.location.routingPoints);
    }
    this.control.setWaypoints(coordinates);

    // Use ngZone to catch all leaflet and leaflet routing machine errors
    this.ngZone.runGuarded(() => this.control.route());
  }

  protected getLocationById(locationId: number): void {
    this.isLoading = true;
    this.siterecordService.getSiterecordById(locationId).subscribe(location => {
      this.location = location;
      if (!this.isContractDataproviderTypeMst()) {
        if (this.location.currentAlertcPoints.length === 0) {
          this.location.currentAlertcPoints.push(this.initializeAlertcPoint(this.currentVildVersion));
          this.isCurrentAlertcAdded = true;
        }
        if (this.futureVildVersion) {
          if (this.location.futureAlertcPoints.length === 0) {
            this.location.futureAlertcPoints.push(this.initializeAlertcPoint(this.futureVildVersion));
            this.isFutureAlertcAdded = true;
          }
        }
      }

      this.originalLocation = JSON.stringify(location);

      if (this.location.openlrStructure) {
        this.openlr = JSON.parse(this.location.openlrStructure.openlr).openlrExtendedPoint;
      }
      this.initializeReadonly();
      this.initializeTranslations();
      this.initializeLegend();
      this.drawPoints();
      this.isLoading = false;
    });
  }

  public missingDirection(): boolean {
    if (!this.readonly && !this.contractIsRwsShapesContract() && !this.isContractTypeDataProvider()) {
      return this.arrowHead == null;
    }
    return false;
  }

  public onCancel(): void {
    if (this.isEditPage) {
      this.goBackToOverview();
    } else {
      this.isCanceling = true;
      this.readonly = !this.readonly;
      this.location = JSON.parse(this.originalLocation);
      this.isCanceling = false;
      this.deleteLineAndArrowHead();

      if (this.isContractDataproviderTypeMst() && !this.contractIsRwsShapesContract()) {
        this.adminRoutingPointsLayerGroup.addTo(this.map);
        this.layerControl.addOverlay(this.adminRoutingPointsLayerGroup, this.adminRoutingPointsTranslation);
      }
      if (this.readonly && this.isAllowedToEditRoute()) {
        this.initializeControl(false);
      }
    }
  }

  protected onEdit(): void {
    if (this.isAllowedToEditRoute()) {
      // Clear controls waypoints
      this.control.spliceWaypoints(0, this.control.getWaypoints().length);

      if (this.adminRoutingPointsLayerGroup) {
        this.map.removeLayer(this.adminRoutingPointsLayerGroup);
        this.layerControl.removeLayer(this.adminRoutingPointsLayerGroup);
      }
      if (this.isContractDataproviderTypeMst()) {
        this.initializeControl(!this.readonly, this.ADMIN_ROUTING_POINTS_STYLE);
      } else {
        this.initializeControl(!this.readonly, this.NEW_SHAPE_STYLE);
      }
      this.createRoute();

      if (!this.readonly) {
        this.control.on('waypointschanged', e => {
          if (e.waypoints[0].latLng && e.waypoints[1].latLng && this.map.hasLayer(this.line)) {
            this.map.removeLayer(this.arrowHead);
            this.map.removeLayer(this.line);
            this.arrowHead = null;
            this.line = null;
            if (!this.isCoordinateEditMode) {
              this.control.setWaypoints([L.latLng(e.waypoints[0].latLng.lat, e.waypoints[0].latLng.lng)]);
            }
          }
          // Use ngZone to catch all leaflet and leaflet routing machine errors
          this.ngZone.runGuarded(() => this.control.route());
        });

        this.map.on('click', (e: any) => {
          if (!this.control.getWaypoints()[0].latLng) {
            this.control.setWaypoints([L.latLng(e.latlng.lat, e.latlng.lng)]);
          } else if (!this.control.getWaypoints()[1].latLng) {
            this.control.spliceWaypoints(this.control.getWaypoints().length - 1, 1, L.latLng(e.latlng));
          }
        });
      }
    }
  }

  public onSave(): void {
    if (this.isCommentMandatory()) {
      this.dialogCaptureCommentComponent.show();

      return;
    }

    this.isSaving = true;
    this.errorMessages = [];

    const copyOfLocation = JSON.parse(JSON.stringify(this.location));
    this.setSiterecordParts(copyOfLocation);
    this.updateSiterecord(copyOfLocation);
  }

  public setSiterecordParts(copyOfLocation: Siterecord): void {
    if (!this.isLocationAdmin()) {
      console.warn('setSiterecordParts skipped because the user is not admin');
      return;
    }

    if (this.location.contract.dataproviderType === DataProviderType.MST) {
      console.warn('setSiterecordParts skipped because the contract type is not MST');
      return;
    }

    copyOfLocation.currentAlertcPoints[0] = this.pointDetailsVild.first.getAlertcPoint();

    if (this.pointDetailsVild.first.getAlertcPoint() !== this.pointDetailsVild.last.getAlertcPoint()) {
      copyOfLocation.futureAlertcPoints[0] = this.pointDetailsVild.last.getAlertcPoint();
    }

    if (this.isCurrentAlertcAdded) {
      const hasDifferences = this.hasAlertcValues(copyOfLocation.currentAlertcPoints);
      if (!hasDifferences) {
        copyOfLocation.currentAlertcPoints.splice(0, 1);
      }
    }

    if (this.isFutureAlertcAdded) {
      const hasDifferences = this.hasAlertcValues(copyOfLocation.futureAlertcPoints);
      if (!hasDifferences) {
        copyOfLocation.futureAlertcPoints.splice(0, 1);
      }
    }

    this.setAlertcPoints(copyOfLocation);
    this.determineVildStatusManual(copyOfLocation);
  }

  public onSaveAndApprove(status: ConfigurationStatusType) {
    if (this.isCommentMandatory()) {
      this.dialogCaptureCommentComponent.show();

      return;
    }

    this.isSaving = true;
    this.errorMessages = [];

    const copyOfLocation = JSON.parse(JSON.stringify(this.location));
    this.setSiterecordParts(copyOfLocation);
    this.updateSiterecordAndStatus(copyOfLocation, status);
  }

  public onSaveWithComment(comment: SiterecordComment): void {
    this.isSaving = true;
    this.errorMessages = [];

    if (comment && comment.comment) {
      const copyOfLocation = JSON.parse(JSON.stringify(this.location));
      this.setSiterecordParts(copyOfLocation);
      this.updateSiterecordWithComment(copyOfLocation, comment.comment, comment.internal);
    }
  }

  public showEditLocationIdModal() {
    this.dialogEditLocationIdComponent.show();
  }

  private setAlertcPoints(location: Siterecord): void {
    const newAlertcPoint = [];
    this.pointDetailsVild.forEach(pdv => newAlertcPoint.push(pdv.getAlertcPoint()));
    location.alertcPoints = newAlertcPoint;
  }

  private determineVildStatusManual(location: Siterecord): void {
    const originalLocation = JSON.parse(this.originalLocation);
    let hasDifferences = this.getDifferences(location.currentAlertcPoints, originalLocation.currentAlertcPoints);
    if (hasDifferences) {
      this.setStatusManual(location.currentAlertcPoints);
    }

    hasDifferences = this.getDifferences(location.futureAlertcPoints, originalLocation.futureAlertcPoints);
    if (hasDifferences) {
      this.setStatusManual(location.futureAlertcPoints);
    }
  }

  private hasAlertcValues(alertc: AlertcPoint[]): boolean {
    for (const a of alertc) {
      if (
        a.directionCoded ||
        a.offsetDistance ||
        a.carriageway ||
        a.displayLatitude ||
        a.displayLongitude ||
        (a.vildLocation && a.vildLocation.locNr)
      ) {
        return true;
      }
    }
    return false;
  }

  private getDifferences(newAlertc: AlertcPoint[], originalAlertc: AlertcPoint[]): boolean {
    if (newAlertc.length !== originalAlertc.length) {
      return true;
    } else {
      for (let i = 0; i < newAlertc.length; i++) {
        if (
          newAlertc[i].directionCoded !== originalAlertc[i].directionCoded ||
          newAlertc[i].offsetDistance !== originalAlertc[i].offsetDistance ||
          newAlertc[i].carriageway !== originalAlertc[i].carriageway ||
          (newAlertc[i].vildLocation != null &&
            newAlertc[i].vildLocation.locNr &&
            (originalAlertc[i].vildLocation === null ||
              newAlertc[i].vildLocation.locNr !== originalAlertc[i].vildLocation.locNr))
        ) {
          return true;
        }
      }
    }
    return false;
  }

  private setStatusManual(alertcPoints: AlertcPoint[]) {
    alertcPoints.forEach(acp => {
      acp.vildEncodingStatus = VildEncodingStatusType.MANUAL;
    });
  }

  private isCommentMandatory(): boolean {
    const originalLocation = JSON.parse(this.originalLocation);

    return this.location.routingPoints !== originalLocation.routingPoints;
  }

  protected removeLayers(): void {
    this.deleteLineAndArrowHead();

    if (this.routingPointsLayer) {
      this.layerControl.removeLayer(this.routingPointsLayer);
    }
    if (this.previousRoutingPointsLayer) {
      this.layerControl.removeLayer(this.previousRoutingPointsLayer);
    }
    if (this.adminRoutingPointsLayerGroup) {
      this.layerControl.removeLayer(this.adminRoutingPointsLayerGroup);
    }
    if (this.map.hasLayer(this.adminRoutingPointsLayerGroup)) {
      this.map.removeLayer(this.adminRoutingPointsLayerGroup);
    }
    if (this.pointShapeLayerGroup) {
      this.layerControl.removeLayer(this.pointShapeLayerGroup);
    }
    if (this.map.hasLayer(this.pointShapeLayerGroup)) {
      this.map.removeLayer(this.pointShapeLayerGroup);
    }
    if (this.previousPointShapeLayerGroup) {
      this.layerControl.removeLayer(this.previousPointShapeLayerGroup);
    }
    if (this.map.hasLayer(this.previousPointShapeLayerGroup)) {
      this.map.removeLayer(this.previousPointShapeLayerGroup);
    }
  }

  protected initializeLegend(): void {
    let labels = [];
    let colors = [];

    if (this.isContractDataproviderTypeMst()) {
      labels = [
        this.routingPointsTranslation,
        this.previousRoutingPointsTranslation,
        this.shapeTranslation,
        this.previousShapeTranslation,
        this.adminRoutingPointsTranslation
      ];
      colors = [
        this.ROUTING_POINTS_STYLE.lineColor,
        this.PREVIOUS_ROUTING_POINTS_STYLE.lineColor,
        this.SHAPE_STYLE.lineColor,
        this.PREVIOUS_SHAPE_STYLE.lineColor,
        this.ADMIN_ROUTING_POINTS_STYLE.lineColor
      ];
    } else {
      labels = [this.newShapeTranslation, this.shapeTranslation, this.previousShapeTranslation];
      colors = [this.NEW_SHAPE_STYLE.lineColor, this.SHAPE_STYLE.lineColor, this.PREVIOUS_SHAPE_STYLE.lineColor];
    }

    this.createLegend(labels, colors);
  }

  protected initializeTranslations(): void {
    if (!this.isContractDataproviderTypeMst()) {
      this.translate('LOCATIONS.LEGEND.NEWPOINT').subscribe(translation => (this.newShapeTranslation = translation));

      this.translate('LOCATIONS.LEGEND.POINTSHAPE').subscribe(translation => (this.shapeTranslation = translation));

      this.translate('LOCATIONS.LEGEND.PREVIOUSPOINTSHAPE').subscribe(
        translation => (this.previousShapeTranslation = translation)
      );
    } else if (this.isContractDataproviderTypeMst()) {
      this.translate('LOCATIONS.LEGEND.MSTLOCATION.ROUTINGPOINTS').subscribe(
        translation => (this.routingPointsTranslation = translation)
      );

      this.translate('LOCATIONS.LEGEND.MSTLOCATION.PREVIOUSROUTINGPOINTS').subscribe(
        translation => (this.previousRoutingPointsTranslation = translation)
      );

      this.translate('LOCATIONS.LEGEND.MSTLOCATION.POINTSHAPE').subscribe(
        translation => (this.shapeTranslation = translation)
      );

      this.translate('LOCATIONS.LEGEND.MSTLOCATION.PREVIOUSPOINTSHAPE').subscribe(
        translation => (this.previousShapeTranslation = translation)
      );

      this.translate('LOCATIONS.LEGEND.MSTLOCATION.ADMINROUTINGPOINTS').subscribe(
        translation => (this.adminRoutingPointsTranslation = translation)
      );
    }

    this.translate('ERROR.FE003').subscribe(translation => (this.noRouteFoundTranslation = translation));

    this.translate('ERROR.FE004').subscribe(translation => (this.failedSaveTranslation = translation));
  }

  private initializeAlertcPoint(vildVersion: VildVersion): AlertcPoint {
    const point = new AlertcPoint();
    point.siterecordId = this.location.id;
    point.vildVersion = vildVersion;
    point.vildEncodingStatus = VildEncodingStatusType.ERROR;
    point.locationCountryCode = '8';
    return point;
  }

  public showRollbackDeletedModal() {
    this.dialogConfirmationComponent.show();
  }

  public showEditCoordinatesModal() {
    this.dialogEditCoordinatesComponent.show();
  }
}



// WEBPACK FOOTER //
// ./src/app/locations/details/point/point-details.component.ts